// mission-initializer-lock.ts
//
// Split off mission-initializer.ts (≤200 line budget): exclusive init lock,
// `<control_root>/locks/init.lock`, O_EXCL-with-pid pattern (mirrors
// event-journal.ts's approach at a high level, but is its own small
// self-contained helper — mission-lock.ts/event-journal.ts are NOT modified
// or imported here per the phase's do-not-touch list).
//
// Same operation id retrying after a kill reclaims the stale lock (init is
// meant to be idempotent-by-retry, test 10); a DIFFERENT operation id trying
// to init concurrently is rejected.

import { promises as fs } from "node:fs";
import { join } from "node:path";
import { ensureDir, readJson } from "../filesystem-helpers.js";

export class InitLockBusyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InitLockBusyError";
  }
}

interface InitLockFile {
  pid: number;
  operationId: string;
}

async function writeLockFile(lockPath: string, operationId: string): Promise<void> {
  const handle = await fs.open(lockPath, "wx", 0o600);
  try {
    await handle.writeFile(`${JSON.stringify({ pid: process.pid, operationId })}\n`);
  } finally {
    await handle.close();
  }
}

/** Acquire the exclusive init lock. Returns a release function. */
export async function acquireInitLock(
  controlRoot: string,
  operationId: string,
): Promise<() => Promise<void>> {
  const lockDir = join(controlRoot, "locks");
  await ensureDir(lockDir);
  const lockPath = join(lockDir, "init.lock");

  try {
    await writeLockFile(lockPath, operationId);
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code !== "EEXIST") throw err;

    const existing = await readJson<InitLockFile>(lockPath).catch(() => undefined);
    if (existing?.operationId !== operationId) {
      throw new InitLockBusyError(
        `Init lock đang bị giữ bởi operation khác (${existing?.operationId ?? "unknown"}) tại ${lockPath}.`,
      );
    }
    // Same operation, stale lock from a killed run — reclaim.
    await fs.unlink(lockPath).catch(() => {});
    await writeLockFile(lockPath, operationId);
  }

  return async () => {
    await fs.unlink(lockPath).catch(() => {});
  };
}
